import styled from '@emotion/styled';
import { MaxMobileWindowWidth, RacemapColors } from '@racemap/utilities/consts/common';
import { Button, Checkbox, Input, Space, Typography } from 'antd';
import type { FuseOptionKey } from 'fuse.js';
import type { ReactNode } from 'react';
import { useFuzzySearch } from 'src/lib/customHooks/useFuzzySearch';

const { Text } = Typography;

interface PickerItem<K extends string, G extends string> {
  key: K;
  label: ReactNode;
  group: G;
  searchText: string;
}

interface Props<K extends string, G extends string> {
  title?: string;
  items: Array<PickerItem<K, G>>;
  groups: Record<G, { label: string }>;
  selected: ReadonlySet<K>;
  onChange: (selected: Set<K>) => void;
  onReset: () => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  searchPlaceholder?: string;
  emptyText?: string;
}

const FUZZY_SEARCH_FIELDS = ['searchText'];

/** Searchable checkbox list in groups, each with All/None. Meant as a Popover body. */
export function GroupedCheckboxPicker<K extends string, G extends string>({
  title,
  items,
  groups,
  selected,
  onChange,
  onReset,
  searchTerm,
  onSearchTermChange,
  searchPlaceholder = 'Search...',
  emptyText = 'Nothing found',
}: Props<K, G>) {
  const found = useFuzzySearch(
    items,
    searchTerm,
    FUZZY_SEARCH_FIELDS as Array<FuseOptionKey<PickerItem<K, G>>>,
    150,
  );

  const setKeys = (keys: Array<K>, checked: boolean) => {
    const next = new Set(selected);
    for (const key of keys) {
      if (checked) next.add(key);
      else next.delete(key);
    }
    onChange(next);
  };

  return (
    <Content>
      <Header>
        <Text strong>{title}</Text>
        <Space size={4}>
          <Text type="secondary">
            {selected.size}/{items.length}
          </Text>
          <Button size="small" type="link" onClick={onReset}>
            Reset
          </Button>
        </Space>
      </Header>

      <Input
        placeholder={searchPlaceholder}
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        size="small"
        allowClear
      />

      <GroupList>
        {(Object.entries(groups) as Array<[G, { label: string }]>).map(([groupId, group]) => {
          const groupItems = found.filter((item) => item.group === groupId);
          if (!groupItems.length) return null;
          const groupKeys = groupItems.map((item) => item.key);

          return (
            <GroupSection key={groupId}>
              <GroupHeader>
                <Text type="secondary">{group.label}</Text>
                <GroupActions>
                  <Button size="small" type="link" onClick={() => setKeys(groupKeys, true)}>
                    All
                  </Button>
                  <Button size="small" type="link" onClick={() => setKeys(groupKeys, false)}>
                    None
                  </Button>
                </GroupActions>
              </GroupHeader>

              <ItemsGrid>
                {groupItems.map((item) => (
                  <Checkbox
                    key={item.key}
                    checked={selected.has(item.key)}
                    onChange={(e) => setKeys([item.key], e.target.checked)}
                  >
                    {item.label}
                  </Checkbox>
                ))}
              </ItemsGrid>
            </GroupSection>
          );
        })}
        {found.length === 0 && <Text type="secondary">{emptyText}</Text>}
      </GroupList>
    </Content>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(420px, 80vw);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GroupList = styled.div`
  max-height: min(50vh, 500px);
  overflow-y: auto;
`;

const GroupSection = styled.div`
  padding: 4px 0 8px;
  border-bottom: 1px solid ${RacemapColors.LightGray};

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GroupActions = styled.div`
  display: flex;

  button {
    padding: 0 4px;
    font-size: 11px;
    height: auto;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 12px;
  row-gap: 4px;

  @media (max-width: ${MaxMobileWindowWidth}px) {
    grid-template-columns: 1fr;
  }
`;
